import React, { useEffect, useRef, useState } from 'react';
import { Checkbox, Table } from 'antd';
import { ColumnsType } from 'antd/es/table';
import classNames from 'classnames';
import NameBadge from 'features/Workspace/components/NameBadge';
import {
  JobRFQOrgItemViewer,
  JobRFQQtyItemRevisionViewer,
  JobRFQRecipientViewer,
  JobRFQShippingItemViewer,
  JobRFQViewer,
  RFQOrgStatusEnum,
  JobConfigFileItem,
} from 'domains/jobconfigDomain';
import { CheckboxMatrix } from './TabSelectSuppliers';
import { clone, isArray, omit } from 'lodash-es';
import { CheckboxChangeEvent } from 'antd/lib/checkbox/Checkbox';
import { useTranslation } from 'react-i18next';
import { RevxMoney } from 'components/RevxMoney';
import ShippingMethodTable from 'features/Workspace/components/ShippingMethodTable';
import { formatNumberToThousandths } from 'utils/number';
import { RevxButton } from 'components/RevxButton';
import workspaceService from 'services/jobService';
import moment from 'moment';
import { DEFAULT_DATE_FORMAT } from 'components/RevxDate';
import {
  DEFAULT_PLACEHOLDER,
  UPLOAD_FILE_ADDRESS_PREFIX,
} from 'utils/constants';
import { toast } from 'components/RevxMessage';
import { TabsKeys } from '../index';
import { NoteIcon } from 'components/Icons';
import { JobItem } from 'domains/workspaceDomain';
import { FileLink } from 'components/RevxFileLink';

const RFQ_JOB_STATUS_MAP: Partial<Record<RFQOrgStatusEnum, string>> = {
  [RFQOrgStatusEnum.DECLINED]: 'Declined',
  [RFQOrgStatusEnum.TO_REVIEW]: 'No Action',
  [RFQOrgStatusEnum.AWARDED]: 'Awarded',
  [RFQOrgStatusEnum.NOT_AWARDED]: 'Not Awarded',
};
const EXPAND_COLUMNS_CLASS = 'expanded-row-open';
const EXPAND_TABLE_SHIPPING_ROW_ID = 'Shipping Method';
const SHIPPING_VISIBLE_HANDLER_CLASS = 'shipping-handler';

const canViewResponseDetails = (data: JobRFQOrgItemViewer) => {
  return (
    [
      RFQOrgStatusEnum.IN_PROCESS,
      RFQOrgStatusEnum.AWARDED,
      RFQOrgStatusEnum.NOT_AWARDED,
    ].includes(data.rfqOrgStatus) ||
    (data.rfqOrgStatus === RFQOrgStatusEnum.TO_REVIEW && data.isConfirmation)
  );
};

const sortResponsesOrgItems = (orgItems?: JobRFQOrgItemViewer[]) => {
  if (!orgItems) return orgItems;

  return [...orgItems].sort((left, right) => {
    const leftRank = left.rfqOrgStatus === RFQOrgStatusEnum.AWARDED ? 0 : 1;
    const rightRank = right.rfqOrgStatus === RFQOrgStatusEnum.AWARDED ? 0 : 1;

    return leftRank - rightRank;
  });
};

type Props = {
  data?: JobRFQViewer;
  jobData: JobItem;
  canUpdateShipping: boolean;
  reloadRfqData: (key?: TabsKeys) => Promise<JobRFQViewer>;
  updateQuoteDate: (data: SubTableMainData[]) => void;
  reloadQuoteDate: () => void;
};
export type SubTableMainData = {
  id: string;
  expandId: string;
  orgId: number;
  supplierOrgName: string;
  supplierPrintingCharge: number;
  revxPrintingMarkup: number;
  revxPrintingMarkupPct: number;
  shippingItems: Array<JobRFQShippingItemViewer>;
  supplierFreightCharge: number;
  revxShippingMarkup: number;
  revxShippingMarkupPct: number;
  supplierCharge: number;
  quantity: number;
  qtySerialNumber: number;
  rfqOrgStatus: RFQOrgStatusEnum;
  revxCharge: number;
  revxPrintingCharge: number;
  revxShippingCharge: number;
  revxFreightCharge: number;
  revxFreightCost: number;
  extraSupplierShippingCharge: number;
  adjustShipDate: string;
  originalShipDate: string;
  supplierNote: string;
  sourcerNote: string;
  supplierCompanyName: string;
  supplierFiles: JobConfigFileItem[];
};
type SubTableData = {
  [x: number]: {
    mainData: Array<SubTableMainData>;
    mockData: Array<SubTableMainData>;
  };
};
const TABLE_QUANTITY_WIDTH: { [key: number]: number } = {
  '1': 500,
  '2': 250,
  '3': 150,
  '4': 125,
  '5': 100,
};

const TabResponses: React.FC<Props> = ({
  data,
  jobData,
  canUpdateShipping,
  reloadRfqData,
  updateQuoteDate,
  reloadQuoteDate,
}) => {
  const [checkboxMatrixData, setCheckboxMatrixData] =
    useState<CheckboxMatrix>();
  const [responsesColumns, setResponsesColumns] =
    useState<ColumnsType<JobRFQOrgItemViewer>>();
  const [mainTableData, setMainTableData] = useState<JobRFQOrgItemViewer[]>();
  const [subTableData, setSubTableData] = useState<SubTableData>();
  const [subTableColumns, setSubTableColumns] =
    useState<ColumnsType<SubTableMainData>>();
  const [dynamicCellClass, setDynamicCellClass] = useState<string>('');
  const [exKeys, setExKeys] = useState<string[]>([]);
  const [mainTableExpendKey, setMainTableExpendKey] = useState<number[]>([]);
  const [currentSelectRecord, setCurrentSelectRecord] =
    useState<SubTableMainData>();
  const [expandPopArrowPosition, setExpandPopArrowPosition] =
    useState<number>();
  const [tableQuantityWidth, setTableQuantityWidth] = useState<number>();
  const currentSelectRecordSerialNumberRef = useRef({
    orgId: -1,
    serialNumber: -1,
  });
  const [expandedRevisionKeys, setExpandedRevisionKeys] = useState<string[]>([]);
  const shippingDivRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  useEffect(() => {
    setMainTableData(sortResponsesOrgItems(data?.orgItems));
    setDynamicCellClass(
      `table-cell-dynamic${data?.quantities.length || 1} table-cell-dynamic`
    );
    setTableQuantityWidth(TABLE_QUANTITY_WIDTH[data?.quantities.length || 1]);
    genMatrixList();
    genExpandData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);
  useEffect(() => {
    genResponsesTableColumns();
    formatQuoteBuilderData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkboxMatrixData]);
  useEffect(() => {
    genExpandColumns();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subTableData]);

  const genMatrixList = () => {
    if (data) {
      const { orgItems, quantities } = data;
      let tmp: CheckboxMatrix = {};
      orgItems.forEach((i) => {
        tmp[i.supplierOrgId] = new Array(quantities.length).fill({
          checked: false,
        });
      });
      setCheckboxMatrixData(tmp);
    }
  };
  const responsesColumnsColSpan = (
    record: JobRFQOrgItemViewer,
    recordIndex: number,
    isFirstCol: boolean,
    /* if the quantity is 1, it is necessary to determine whether the expansion logic is currently being processed */
    rowLogic?: boolean
  ) => {
    let num: number;
    let colSpanCount = data?.quantities.length;
    if (!canExpandable(record)) {
      if (rowLogic) {
        colSpanCount = 2;
      }
      if (isFirstCol) {
        num = colSpanCount || 5;
      } else {
        num = 0;
      }
    } else {
      num = 1;
    }
    return num;
  };

  const toggleMainTableExpandRowHandler = (id: number) => {
    setMainTableExpendKey((prev) => {
      if (prev.includes(id)) {
        return prev.filter((item) => item !== id);
      }

      return [...prev, id];
    });
  };

  const genResponsesTableColumns = () => {
    if (data) {
      const { quantities } = data;
      const dynamicColumn: ColumnsType<JobRFQOrgItemViewer> = [];
      quantities.forEach((item, quantitiesIndex) => {
        const isFirstQuantityCol = quantitiesIndex === 0;
        dynamicColumn.push({
          title: (
            <div
              className={classNames('table-cell-title', {
                'first-col-title': isFirstQuantityCol,
              })}
            >
              <p>Quantity {quantitiesIndex + 1}</p>
              <p className="table-title-weight">
                {formatNumberToThousandths(item)}
              </p>
            </div>
          ),
          width: tableQuantityWidth,
          dataIndex: 'quantity',
          sorter: (left, right) => {
            const leftPrice = left.qtyItems?.[quantitiesIndex]?.revxCharge || 0;
            const rightPrice = right.qtyItems?.[quantitiesIndex]?.revxCharge || 0;
            return leftPrice - rightPrice;
          },
          render: (value, record, index) => {
            const cantSelect = record.qtyItems[
              quantitiesIndex
            ]?.shippingItems.some((u) => !u.shippingMethodAllowed);
            const quantityStatus = record.qtyItems[quantitiesIndex]?.rfqOrgStatus;
            const isExpandedRow =
              responsesColumnsColSpan(
                record,
                index,
                isFirstQuantityCol,
                true
              ) === 1;
            const canSelectCol =
              quantityStatus ===
                RFQOrgStatusEnum.IN_PROCESS ||
              (record.rfqOrgStatus === RFQOrgStatusEnum.TO_REVIEW &&
                record.isConfirmation);
            const canShowPrice = canViewResponseDetails(record);
            const quantityStatusLabel =
              record.rfqOrgStatus === RFQOrgStatusEnum.AWARDED ||
              record.rfqOrgStatus === RFQOrgStatusEnum.NOT_AWARDED
                ? RFQ_JOB_STATUS_MAP[record.rfqOrgStatus]
                : null;
            const noActionStyle =
              record.rfqOrgStatus === RFQOrgStatusEnum.TO_REVIEW &&
              !record.isConfirmation;
            const declinedStyle =
              record.rfqOrgStatus === RFQOrgStatusEnum.DECLINED;
            const awardedStyle = record.rfqOrgStatus === RFQOrgStatusEnum.AWARDED;
            const notAwardedStyle =
              record.rfqOrgStatus === RFQOrgStatusEnum.NOT_AWARDED;
            return {
              children: (
                <div
                  className={classNames('quantity-wrap', {
                    expanded: isExpandedRow,
                    noActionStyle,
                    declinedStyle,
                    awardedStyle,
                    notAwardedStyle,
                  })}
                >
                  {isExpandedRow ? (
                    canShowPrice ? (
                      <div className={classNames(dynamicCellClass, 'response-price-status')}>
                        {canSelectCol && (
                          <Checkbox
                            disabled={cantSelect}
                            checked={
                              checkboxMatrixData?.[record?.supplierOrgId]?.[
                                quantitiesIndex
                              ].checked
                            }
                            onChange={(e) => {
                              quantityCheckboxChangeHandler(
                                record.supplierOrgId,
                                quantitiesIndex,
                                e
                              );
                            }}
                          />
                        )}
                        <div className="checkbox-wrap response-price-status__content">
                          <span className="checkbox-wrap__price">
                            $
                            {formatNumberToThousandths(
                              record.qtyItems[quantitiesIndex].revxCharge,
                              { minimumFractionDigits: 2 }
                            )}
                          </span>
                          {quantityStatusLabel && (
                            <span className="response-status-label">
                              {quantityStatusLabel}
                            </span>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className={dynamicCellClass}>
                        <div className="table-cell-placeholder">
                          {DEFAULT_PLACEHOLDER}
                        </div>
                      </div>
                    )
                  ) : (
                    <span>
                      {RFQ_JOB_STATUS_MAP[record.rfqOrgStatus] ||
                        DEFAULT_PLACEHOLDER}
                    </span>
                  )}
                </div>
              ),
              props: {
                colSpan: responsesColumnsColSpan(
                  record,
                  index,
                  isFirstQuantityCol
                ),
              },
            };
          },
        });
      });
      const firstTableColumns: ColumnsType<JobRFQOrgItemViewer> = [
        {
          title: (
            <div className="table-cell-first table-title-weight">Supplier</div>
          ),
          dataIndex: 'supplierOrgName',
          width: 100,
          sorter: (left, right) => {
            const leftName = (left.supplierOrgName || '').toLowerCase();
            const rightName = (right.supplierOrgName || '').toLowerCase();
            return leftName.localeCompare(rightName);
          },
          render: (value, record) => {
            return (
              <div className="table-cell-first supplier-name-wrapper">
                <span>{value}</span>
                {(record.supplierNote || record.sourcerNote) &&
                  canExpandable(record) && (
                    <span
                      className="note-svg-wrapper"
                      onClick={(e) => {
                        const { id } = record;
                        toggleMainTableExpandRowHandler(id);
                      }}
                    >
                      <NoteIcon width={18} height={20} fill="#fff" />
                    </span>
                  )}
              </div>
            );
          },
        },
        {
          title: <div className="table-title-weight">Date Sent</div>,
          dataIndex: 'sendAt',
          width: 100,
          render: (value, r) => {
            return (
              <div style={{ width: 100 }}>
                {r.isResent && <p>Resent:</p>}
                <p>{moment(value).format(DEFAULT_DATE_FORMAT)}</p>
              </div>
            );
          },
        },
        {
          title: <div className="table-title-weight">Date Received</div>,
          dataIndex: 'responseAt',
          width: 100,
          sorter: (left, right) => {
            const leftTime = left.responseAt
              ? moment(left.responseAt).valueOf()
              : 0;
            const rightTime = right.responseAt
              ? moment(right.responseAt).valueOf()
              : 0;
            return leftTime - rightTime;
          },
          render: (value) => {
            return (
              <div style={{ width: 100 }}>
                {value
                  ? moment(value).format(DEFAULT_DATE_FORMAT)
                  : DEFAULT_PLACEHOLDER}
              </div>
            );
          },
        },
      ];
      const lastTableColumns: ColumnsType<JobRFQOrgItemViewer> = [
        {
          title: <div className="table-title-weight">Contacts</div>,
          dataIndex: 'recipients',
          width: 110,
          render: (value: JobRFQRecipientViewer[]) => {
            return (
              <div className="table-cell-operate">
                <div className="table-cell-operate__left">
                  {value?.map((i, index: number) => {
                    if (!i.checked) {
                      return null;
                    }
                    return (
                      <div className="name-badge-wrapper" key={index}>
                        <NameBadge name={i.firstName + ' ' + i.lastName} />
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          },
        },
        {
          title: <div className="table-title-weight">Action</div>,
          render: (value: JobRFQOrgItemViewer) => {
            const { id, hasUpdateShippingDraft } = value;
            return (
              <RevxButton
                brandGreen={true}
                className="action-btn"
                onClick={async () => {
                  if (canUpdateShipping && !hasUpdateShippingDraft) {
                    await workspaceService.updateRfqJobShipping(data?.id, id);
                    await reloadRfqData(TabsKeys.determine);
                    await reloadQuoteDate();
                  } else {
                    if (hasUpdateShippingDraft) {
                      toast.error(t('workspace.rfq.hasResendTips'));
                    } else {
                      toast.error(t('workspace.rfq.responseTips'));
                    }
                  }
                }}
              >
                <span style={{ fontWeight: 'normal', fontSize: 13 }}>
                  Update Shipping
                </span>
              </RevxButton>
            );
          },
        },
      ];
      setResponsesColumns([
        ...firstTableColumns,
        ...dynamicColumn,
        ...lastTableColumns,
      ]);
    }
  };
  const subTableFirstColValueMap = [
    'Original Ship Date',
    'Adjusted Ship Date',
    'Printing Cost',
    EXPAND_TABLE_SHIPPING_ROW_ID,
    'Shipment Cost',
    'Job Net Total',
    'Notes to ',
    'Notes from ',
    'Files from ',
  ];
  const subTableFirstColValueMapLen = subTableFirstColValueMap.length - 1;
  const isGenericRow = (index: number) => {
    return (
      index === subTableFirstColValueMapLen ||
      index === subTableFirstColValueMapLen - 1 ||
      index === subTableFirstColValueMapLen - 2
    );
  };
  const genExpandData = () => {
    if (data) {
      const processOrgItems: JobRFQOrgItemViewer[] = data?.orgItems.filter(
        (i) =>
          canViewResponseDetails(i)
      );
      const expandData: SubTableData = {};
      processOrgItems.forEach((i: JobRFQOrgItemViewer, index: number) => {
        const {
          supplierOrgName,
          supplierNote,
          sourcerNote,
          supplierCompanyName,
          supplierFiles,
        } = i;
        const mainData: SubTableMainData[] = i.qtyItems.map((u, uIndex) => {
          const {
            supplierPrintingCharge,
            shippingItems,
            supplierCharge,
            supplierFreightCharge,
            revxShippingMarkup,
            revxShippingMarkupPct,
            revxPrintingMarkup,
            revxPrintingMarkupPct,
            quantity,
            qtySerialNumber,
            rfqOrgStatus,
            revxCharge,
            revxPrintingCharge,
            revxShippingCharge,
            revxFreightCharge,
            revxFreightCost,
            extraSupplierShippingCharge,
          } = u;
          const originalShipDate = moment(
            shippingItems[0]?.originalShipDate || ''
          ).format(DEFAULT_DATE_FORMAT);
          const adjustShipDate = moment(
            shippingItems[0]?.adjustShipDate || ''
          ).format(DEFAULT_DATE_FORMAT);
          return {
            id: `main${uIndex}${index}`,
            expandId: genExpandPopoverKey(
              EXPAND_TABLE_SHIPPING_ROW_ID,
              i.supplierOrgId
            ),
            orgId: i.supplierOrgId,
            supplierOrgName,
            supplierPrintingCharge,
            revxPrintingMarkup,
            revxPrintingMarkupPct,
            shippingItems,
            supplierFreightCharge,
            revxShippingMarkup,
            revxShippingMarkupPct,
            supplierCharge,
            quantity,
            qtySerialNumber,
            rfqOrgStatus,
            revxCharge,
            revxPrintingCharge,
            revxShippingCharge,
            revxFreightCharge,
            revxFreightCost,
            extraSupplierShippingCharge,
            originalShipDate,
            adjustShipDate,
            supplierNote,
            sourcerNote,
            supplierCompanyName,
            supplierFiles,
          };
        });
        const mockData: SubTableMainData[] = subTableFirstColValueMap.map(
          (i, index) => {
            if (isGenericRow(index)) {
              if (
                index === subTableFirstColValueMapLen - 2 ||
                index === subTableFirstColValueMapLen - 1 ||
                index === subTableFirstColValueMapLen
              )
                i += supplierOrgName;
            }
            if (i === 'Shipment Cost' && mainData[0]?.shippingItems[0]?.shippingInfo?.carrierService === 'Supplier Shipping/Delivery') {
              i = 'Supplier Shipment Cost';
            }
            return { ...mainData[0], ...{ id: i } };
          }
        );
        expandData[i.supplierOrgId] = { mainData, mockData };
      });
      setSubTableData(expandData);
    }
  };
  const genExpandPopoverKey = (id: string, orgId: number) => {
    return `${id}-${orgId}`;
  };
  const clickAwayHandler = (event: any) => {
    if (event.target.className === SHIPPING_VISIBLE_HANDLER_CLASS) return;
    if (!shippingDivRef?.current?.contains(event.target)) {
      setExKeys([]);
    }
  };
  const renderSubTableQuantitiesItem = (
    quantitiesIndex: number,
    recordIndex: number,
    record: SubTableMainData
  ) => {
    const { orgId } = record;
    if (subTableData) {
      const { mainData } = subTableData[orgId];
      const currentRecord = mainData[quantitiesIndex];
      console.log('currentRecord', currentRecord);
      let tmp:
        | number
        | string
        | Array<JobRFQShippingItemViewer>
        | JobConfigFileItem[]
        | React.ReactElement = 0;
      const tmpFormat = (
        value:
          | number
          | Array<JobRFQShippingItemViewer>
          | string
          | JobConfigFileItem[]
      ) => {
        if (currentRecord.rfqOrgStatus === RFQOrgStatusEnum.INIT) {
          return DEFAULT_PLACEHOLDER;
        }
        if (typeof value === 'number') {
          return RevxMoney({ value });
        }
        return value;
      };
      switch (recordIndex) {
        case 0:
          tmp = tmpFormat(currentRecord?.originalShipDate);
          break;
        case 1:
          tmp = tmpFormat(currentRecord?.adjustShipDate);
          break;
        case 2:
          tmp = tmpFormat(currentRecord?.revxPrintingCharge);
          break;
        case 3:
          tmp = tmpFormat(currentRecord.shippingItems);
          break;
        case 4:
          tmp = tmpFormat(currentRecord?.revxFreightCost);
          break;
        case 5:
          tmp = tmpFormat(currentRecord?.revxCharge);
          break;
        case 6:
          tmp = tmpFormat(currentRecord?.sourcerNote);
          break;
        case 7:
          tmp = tmpFormat(currentRecord?.supplierNote);
          break;
        case 8:
          tmp = tmpFormat(currentRecord?.supplierFiles);
          break;
      }
      const tmpIsArray = isArray(tmp);
      if (isArray(tmp)) {
        if (recordIndex === 8) {
          return (
            <div>
              {(tmp as JobConfigFileItem[]).map((f) => {
                return (
                  <FileLink
                    key={f.id}
                    fileItem={
                      { ...f, skuId: f.skuId || 0, createAt: '' } as any
                    }
                  />
                );
              })}
            </div>
          );
        }
        tmp =
          tmp.length > 1
            ? t('workspace.rfq.splitShipment')
            : mainData[quantitiesIndex].shippingItems[0]?.shippingInfo
                ?.carrierService || DEFAULT_PLACEHOLDER;
      }
      return (
        <div className={classNames({ 'shipping-methods-wrap': tmpIsArray })}>
          {tmpIsArray ? (
            <div
              className="pointer"
              onClick={(e) => {
                setExpandPopArrowPosition(
                  e.screenX - (window.screen.width / 2 - 600) - 125 / 2
                );
                document.removeEventListener('click', clickAwayHandler);
                const { orgId, serialNumber } =
                  currentSelectRecordSerialNumberRef.current;
                if (
                  serialNumber === currentRecord.qtySerialNumber &&
                  orgId === currentRecord.orgId
                ) {
                  setExKeys([]);
                  currentSelectRecordSerialNumberRef.current.serialNumber = -1;
                  currentSelectRecordSerialNumberRef.current.orgId = -1;
                } else {
                  setExKeys([currentRecord.expandId]);
                  currentSelectRecordSerialNumberRef.current.serialNumber =
                    currentRecord.qtySerialNumber;
                  currentSelectRecordSerialNumberRef.current.orgId =
                    currentRecord.orgId;
                }
                setCurrentSelectRecord(currentRecord);
                document.addEventListener('click', clickAwayHandler);
              }}
            >
              <div className={SHIPPING_VISIBLE_HANDLER_CLASS}>{tmp}</div>
            </div>
          ) : (
            <div>{tmp}</div>
          )}
        </div>
      );
    }
  };
  const calculateNotesColspan = (i: number, firstCol?: boolean) => {
    const isLastCol = isGenericRow(i);
    if (firstCol) {
      return isLastCol ? 3 + data!.quantities.length : 1;
    }
    return isLastCol ? 0 : 1;
  };
  const genExpandColumns = () => {
    if (!data) return;
    const { quantities } = data;
    const subTableDynamicColumn: ColumnsType<SubTableMainData> = [];
    quantities.forEach((i, quantitiesIndex) => {
      subTableDynamicColumn.push({
        dataIndex: `quantity${i}`,
        width: tableQuantityWidth,
        render: (value, record, recordIndex) => {
          const colSpan = isGenericRow(recordIndex) ? 0 : 1;
          return {
            children: (
              <div className={dynamicCellClass}>
                {renderSubTableQuantitiesItem(
                  quantitiesIndex,
                  recordIndex,
                  record
                )}
              </div>
            ),
            props: {
              colSpan,
            },
          };
        },
      });
    });
    const firstSubTableColumns: ColumnsType<SubTableMainData> = [
      {
        dataIndex: 'id',
        width: 160,
        render: (value) => {
          return <div className="table-cell-first">{value}</div>;
        },
      },
      {
        dataIndex: 'dateSent',
        width: 100,
        render: (v, r, i) => {
          return {
            children: (
              <div className="table-cell-contact notes">
                {i === subTableFirstColValueMapLen - 2 ? (
                  r.sourcerNote || DEFAULT_PLACEHOLDER
                ) : i === subTableFirstColValueMapLen - 1 ? (
                  r.supplierNote || DEFAULT_PLACEHOLDER
                ) : i === subTableFirstColValueMapLen ? (
                  r.supplierFiles?.length ? (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                      {r.supplierFiles.map((f) => (
                        <FileLink
                          key={f.id}
                          fileItem={
                            {
                              ...f,
                              skuId: f.skuId || 0,
                              createAt: '',
                              fileUrl:
                                UPLOAD_FILE_ADDRESS_PREFIX +
                                (f.fileUrl || '').replace(/^\//, ''),
                            } as any
                          }
                        />
                      ))}
                    </div>
                  ) : (
                    DEFAULT_PLACEHOLDER
                  )
                ) : (
                  ''
                )}
              </div>
            ),
            props: {
              colSpan: calculateNotesColspan(i, true),
            },
          };
        },
      },
      {
        dataIndex: 'dateReceived',
        width: 100,
        render: (v, r, i) => {
          return {
            children: <div className="table-cell-contact"></div>,
            props: {
              colSpan: calculateNotesColspan(i),
            },
          };
        },
      },
    ];
    const lastSubTableColumns: ColumnsType<SubTableMainData> = [
      {
        dataIndex: 'contacts',
        width: 110,
        render: (v, r, i) => {
          return {
            children: <div className="table-cell-contact"></div>,
            props: {
              colSpan: calculateNotesColspan(i),
            },
          };
        },
      },
      {
        dataIndex: 'action',
      },
    ];
    setSubTableColumns([
      ...firstSubTableColumns,
      ...subTableDynamicColumn,
      ...lastSubTableColumns,
    ]);
  };
  const toggleRevisionKey = (key: string) => {
    setExpandedRevisionKeys((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const subTableRender = (record: JobRFQOrgItemViewer) => {
    // Gather revision history entries grouped by revisionNumber
    const allRevisions = record.qtyItems.flatMap(
      (qi) => qi.revisionHistory || []
    );
    const revisionGroups = allRevisions.reduce<
      Record<number, JobRFQQtyItemRevisionViewer[]>
    >((acc, rev) => {
      const key = rev.revisionNumber ?? 0;
      if (!acc[key]) acc[key] = [];
      acc[key].push(rev);
      return acc;
    }, {});
    const sortedRevisionNumbers = Object.keys(revisionGroups)
      .map(Number)
      .sort((a, b) => b - a);

    return (
      <>
        <Table
          className="responses-expanded-table"
          dataSource={subTableData?.[record.supplierOrgId]?.mockData}
          columns={subTableColumns}
          showHeader={false}
          pagination={false}
          rowKey={({ id, orgId }) => {
            return genExpandPopoverKey(id, orgId);
          }}
          rowClassName={(r, index) => {
            return index === subTableFirstColValueMapLen - 2 ? 'total-tr' : '';
          }}
          expandable={{
            expandedRowRender: (record: any) => {
              return (
                <div ref={shippingDivRef} className="shipping-table-wrapper">
                  {currentSelectRecord && (
                    <ShippingMethodTable
                      isSourcer={true}
                      quantity={currentSelectRecord.quantity}
                      quantityIndex={currentSelectRecord.qtySerialNumber}
                      shippingItems={currentSelectRecord.shippingItems}
                      orgId={currentSelectRecord.orgId}
                      qtySerialNumber={currentSelectRecord.qtySerialNumber}
                      generateJobOrgId={data?.orgId}
                      supplierName={currentSelectRecord.supplierOrgName}
                      supplierCompanyName={
                        currentSelectRecord.supplierCompanyName
                      }
                      jobData={jobData}
                    />
                  )}
                  <div
                    className="shipping-table-wrapper__arrow"
                    style={{
                      left: expandPopArrowPosition,
                    }}
                  />
                </div>
              );
            },
            rowExpandable: (record) => {
              return record.id === EXPAND_TABLE_SHIPPING_ROW_ID;
            },
            expandedRowKeys: exKeys,
            onExpandedRowsChange: (record) => {},
            expandIcon: ({ expanded, onExpand, record }) => {
              return <div></div>;
            },
          }}
        />
        {sortedRevisionNumbers.length > 0 && (
          <div className="revision-history-section">
            {sortedRevisionNumbers.map((revNum) => {
              const revItems = revisionGroups[revNum];
              const revKey = `${record.supplierOrgId}_${revNum}`;
              const isExpanded = expandedRevisionKeys.includes(revKey);
              const firstItem = revItems[0];
              const labelDate = firstItem?.readyDate
                ? moment(firstItem.readyDate).format(DEFAULT_DATE_FORMAT)
                : `Revision ${revNum}`;
              return (
                <div key={revKey} className="revision-history-item">
                  <div
                    className="revision-history-toggle"
                    onClick={() => toggleRevisionKey(revKey)}
                  >
                    <i className={`allow revision-history-arrow ${isExpanded ? '' : 'right'}`} />
                    {`RFQ-Response - ${labelDate}`}
                  </div>
                  {isExpanded && (() => {
                      const revRowFields = [
                      { label: 'Printing Cost', field: 'revxPrintingCharge' as keyof JobRFQQtyItemRevisionViewer },
                      { 
                        label: record.qtyItems[0]?.shippingItems[0]?.shippingInfo?.carrierService === 'Supplier Shipping/Delivery' ? 'Supplier Shipment Cost' : 'Shipment Cost', 
                        field: 'revxFreightCost' as keyof JobRFQQtyItemRevisionViewer 
                      },
                      { label: 'Job Net Total', field: 'revxCharge' as keyof JobRFQQtyItemRevisionViewer },
                      { label: 'Supplier Notes', field: 'supplierNote' as keyof JobRFQQtyItemRevisionViewer },
                    ];
                    const revTableColumns: ColumnsType<{ label: string; field: keyof JobRFQQtyItemRevisionViewer }> = [
                      {
                        dataIndex: 'label',
                        width: 160,
                        render: (val) => <div className="table-cell-first">{val}</div>,
                      },
                      ...(data?.quantities || []).map((qty, qi) => ({
                        title: (
                          <div className={classNames('table-cell-title', { 'first-col-title': qi === 0 })}>
                            <p>Quantity {qi + 1}</p>
                            <p className="table-title-weight">{formatNumberToThousandths(qty)}</p>
                          </div>
                        ),
                        width: tableQuantityWidth,
                        dataIndex: 'field',
                        key: `qty_${qi}`,
                        render: (_: any, row: { label: string; field: keyof JobRFQQtyItemRevisionViewer }) => {
                          const revItem = revItems.find((r) => r.qtySerialNumber === (qi + 1)) || revItems[qi];
                          const val = revItem?.[row.field];
                          return (
                            <div className={dynamicCellClass}>
                              {typeof val === 'number'
                                ? <RevxMoney value={val} />
                                : (val as string) || DEFAULT_PLACEHOLDER}
                            </div>
                          );
                        },
                      })),
                    ];
                    return (
                      <Table
                        className="responses-expanded-table revision-history-expanded-table"
                        dataSource={revRowFields}
                        columns={revTableColumns}
                        showHeader={true}
                        pagination={false}
                        rowKey="label"
                      />
                    );
                  })()}
                </div>
              );
            })}
          </div>
        )}
      </>
    );
  };

  const canExpandable = (data: JobRFQOrgItemViewer) => {
    return canViewResponseDetails(data);
    // return data.rfqOrgStatus === RFQOrgStatusEnum.TO_REVIEW;
  };

  const quantityCheckboxChangeHandler = (
    orgId: number,
    quantitiesIndex: number,
    e: CheckboxChangeEvent
  ) => {
    const cloneData = clone(checkboxMatrixData);
    if (cloneData) {
      cloneData[orgId][quantitiesIndex] = {
        checked: !cloneData[orgId][quantitiesIndex].checked,
      };
    }
    /* only one checkbox can be checked per column */
    let omitCloneData: CheckboxMatrix;
    if (e.target.checked) {
      omitCloneData = omit(cloneData, [orgId]);
      Object.keys(omitCloneData).forEach((i) => {
        omitCloneData[i][quantitiesIndex].checked = false;
      });
    }
    setCheckboxMatrixData(cloneData);
  };

  /* get selected orgItems */
  const formatQuoteBuilderData = () => {
    if (checkboxMatrixData) {
      let quoteData: SubTableMainData[] = [];
      Object.keys(checkboxMatrixData).forEach((i) => {
        return checkboxMatrixData[i].forEach((checkbox, cIndex) => {
          if (checkbox.checked && subTableData) {
            quoteData.push(subTableData?.[Number(i)]?.mainData[cIndex]);
          }
        });
      });
      updateQuoteDate(quoteData);
    }
  };
  return (
    <div className="rfq-responses-table">
      <Table
        dataSource={mainTableData}
        columns={responsesColumns}
        pagination={false}
        rowKey="id"
        expandable={{
          expandedRowRender: (record) => {
            return subTableRender(record);
          },
          // defaultExpandAllRows: true,
          expandedRowKeys: mainTableExpendKey,
          rowExpandable: (record) => canExpandable(record),
          expandIcon: ({ expanded, onExpand, record }) => {
            if (canExpandable(record)) {
              return (
                <i
                  className={`allow ${!expanded ? 'right' : ''}`}
                  onClick={(e) => onExpand(record, e)}
                />
              );
            }
          },
          onExpand: (expanded, record) => {
            toggleMainTableExpandRowHandler(record.id);
            const expandRow = document.querySelector(
              `tr[data-row-key="${record.id}"]`
            );
            if (expanded) {
              expandRow?.classList.add(EXPAND_COLUMNS_CLASS);
            } else {
              expandRow?.classList.remove(EXPAND_COLUMNS_CLASS);
            }
          },
        }}
      />
    </div>
  );
};

export default TabResponses;
